import { useMutation } from '@tanstack/react-query';
import type { DashboardAIGenerateRequest } from 'api/models/DashboardAIGenerateRequest';
import type { DashboardAIGenerateResponse } from 'api/models/DashboardAIGenerateResponse';
import { FirmsService } from 'api/services/FirmsService';
import type { DashboardDefinition } from 'common/types/dashboard/dashboardDefinition';

// The endpoint is stateless: it returns a validated definition and never saves one, so there is no
// cache to invalidate here. Persisting is the caller's job (see useDashboardGeneration).
export interface GenerateDashboardResult {
  dashboard_definition: DashboardDefinition;
  message: string;
}

export type SummaryDashboardType = 'firm' | 'valuations';

// dashboard_definition is an untyped JSON blob on the generated model (Django JSONField), so the
// cast to DashboardDefinition happens once, here at the API boundary. The backend validates the
// payload against the same schema the types are generated from before returning it.
const generate = (
  firmId: number,
  summaryType: SummaryDashboardType,
  data: DashboardAIGenerateRequest
): Promise<DashboardAIGenerateResponse> =>
  summaryType === 'valuations'
    ? FirmsService.apiFirmsValuationsDefinitionsDashboardAiGenerateCreate(String(firmId), data)
    : FirmsService.apiFirmsSummaryDefinitionsDashboardAiGenerateCreate(String(firmId), data);

export const useGenerateDashboard = (firmId: number, summaryType: SummaryDashboardType = 'firm') =>
  useMutation<GenerateDashboardResult, Error, DashboardAIGenerateRequest>({
    mutationFn: async data => {
      const response = await generate(firmId, summaryType, data);
      return {
        dashboard_definition: response.dashboard_definition as DashboardDefinition,
        message: response.message,
      };
    },
  });
